import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { TenancyAgreementData } from 'src/lib/fliko/models/agreements/tenancyAgr.data';
import { ConciergeTaskData } from 'src/lib/fliko/models/concierge/concierge.data';
import { DefectData } from 'src/lib/fliko/models/defects/defect.data';
import { IssueData } from 'src/lib/fliko/models/defects/issue.data';
import { EstateData } from 'src/lib/fliko/models/estate/estate.data';
import { EstNotes } from 'src/lib/fliko/models/notes/collections.data';
import { NoteType } from 'src/lib/fliko/models/notes/notes.data';
import { ResidentData } from 'src/lib/fliko/models/person/resident.data';
import { UserData } from 'src/lib/fliko/models/users/user.data';
import { IssueService } from 'src/app/view/defects/service/issue.service';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { AgreementService } from 'src/app/view/users/services/agreement.service';
import { NoteService } from 'src/app/view/users/services/note.service';
import { ResidentService } from 'src/app/view/users/services/resident.service';
import { UserService } from 'src/app/view/users/services/user.service';
import { NoteVM } from '../models/notes.view-model';
import { idMap } from '../operators/idMap.operator';

@Injectable({
  providedIn: 'root'
})
export class NotesDataService {
  private agreementNotes$ = new BehaviorSubject<NoteVM<TenancyAgreementData>[] | null>(null);
  private defectNotes$ = new BehaviorSubject<NoteVM<DefectData>[] | null>(null);
  private issueNotes$ = new BehaviorSubject<NoteVM<IssueData>[] | null>(null);
  private conciergeNotes$ = new BehaviorSubject<NoteVM<ConciergeTaskData>[] | null>(null);
  private residentsNotes$ = new BehaviorSubject<NoteVM<ResidentData>[] | null>(null);
  private estateNotes$ = new BehaviorSubject<NoteVM<EstateData>[] | null>(null);

  private users$ = this.userService.getUsers$().pipe(idMap());

  private subscription$ = new Subscription();

  constructor(
    private userService: UserService,
    private agreementService: AgreementService,
    private issueService: IssueService,
    private residentService: ResidentService,
    private noteService: NoteService,
    private ctxDS: ContextDataService
  ) {
    this.buildNotes(NoteType.AGREEMENT, this.agreementNotes$, this.agreementService.getPrsAgreements$());
    this.buildNotes(NoteType.DEFECT, this.defectNotes$, this.issueService.getDefects$());
    this.buildNotes(NoteType.ISSUE, this.issueNotes$, this.issueService.getIssues$());
    this.buildNotes(NoteType.CONCIERGE, this.conciergeNotes$, this.issueService.getTasks$());
    this.buildNotes(NoteType.LANDLORD, this.residentsNotes$, this.residentService.getResidents$());
    this.buildEstateNotes();
  }

  // Hack: without as any and type declaration TS returns Observable<NoteVM<T>>[]
  public getAgreementNotes$(): Observable<NoteVM<TenancyAgreementData>[]> {
    return this.agreementNotes$.pipe(filter((x) => !!x)) as any;
  }

  public getDefectNotes$(): Observable<NoteVM<DefectData>[]> {
    return this.defectNotes$.pipe(filter((x) => !!x)) as any;
  }

  public getIssueNotes$(): Observable<NoteVM<DefectData>[]> {
    return this.issueNotes$.pipe(filter((x) => !!x)) as any;
  }

  public getConciergeNotes$(): Observable<NoteVM<ConciergeTaskData>[]> {
    return this.conciergeNotes$.pipe(filter((x) => !!x)) as any;
  }

  public getResidentNotes$(): Observable<NoteVM<ResidentData>[]> {
    return this.residentsNotes$.pipe(filter((x) => !!x)) as any;
  }

  public getEstateNotes$(): Observable<NoteVM<EstateData>[]> {
    return this.estateNotes$.pipe(filter((x) => !!x)) as any;
  }

  private buildNotes<T>(type: NoteType, target$: BehaviorSubject<NoteVM<T>[] | null>, context$: Observable<T[]>): void {
    this.subscription$.add(
      combineLatest([this.noteService.getAllNotes$(type), context$.pipe(idMap()), this.users$]).subscribe(
        ([notes, context, users]) => target$.next(this.buildVMs(notes, context, users))
      )
    );
  }

  private buildVMs<T>(notes: EstNotes, context: Map<string, T>, users: Map<string, UserData>): NoteVM<T>[] {
    const vms: NoteVM<T>[] = [];
    for (const refId in notes) {
      for (const id in notes[refId]) {
        const note = notes[refId][id];
        vms.push({ note, context: context.get(refId), user: users.get(note.userId ?? '') });
      }
    }
    return vms;
  }

  private buildEstateNotes(): void {
    this.subscription$.add(
      combineLatest([this.ctxDS.currentEstate$.pipe(filter((x) => !!x)), this.users$])
        .pipe(
          switchMap(([estate, users]) => {
            return this.noteService
              .getNotes$(NoteType.ESTATE, estate!.id)
              .pipe(map((notes) => { return { notes, estate, users };})); // prettier-ignore
          })
        )
        .subscribe((data) => {
          this.estateNotes$.next(
            Object.values(data.notes).map((note) => {
              return { note, context: data.estate!, user: data.users.get(note.userId ?? '') };
            })
          );
        })
    );
  }
}
